/**
 * The live block feed.
 *
 * The site's proof that it is live: blocks appear here the moment the node
 * imports them, with no refresh and no polling. Each row flashes once on
 * arrival — suppressed under `prefers-reduced-motion`, where the numbers alone
 * still carry everything.
 */

import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import type { RecentBlock } from '../api/generated/RecentBlock'
import { ago, height, seconds } from '../lib/format'
import { href } from '../lib/routes'
import { usePinnedMiners } from '../lib/store'

export function BlockTicker({
  blocks,
  chain,
}: {
  blocks: RecentBlock[]
  /** Needed to address a block; null before the first chain has resolved. */
  chain: string | null
}) {
  const { isPinned } = usePinnedMiners()
  const seen = useRef<Set<number>>(new Set())
  // `ago()` is a pure function of the clock, so nothing re-renders it on its
  // own. One interval for the whole list, rather than a timer per row.
  const [, tick] = useState(0)
  useEffect(() => {
    const id = window.setInterval(() => tick((n) => n + 1), 1000)
    return () => window.clearInterval(id)
  }, [])

  const fresh = new Set<number>()
  for (const block of blocks) {
    if (!seen.current.has(block.height)) fresh.add(block.height)
  }
  useEffect(() => {
    for (const block of blocks) seen.current.add(block.height)
  })

  if (blocks.length === 0) {
    return <p className="empty">Waiting for the next block…</p>
  }

  return (
    <div>
      {blocks.map((block) => {
        const mine = isPinned(block.miner)
        // From the backend, not inferred from the string: several real node
        // names begin with the same letter a Quantus address does, so
        // `quanpool-payout-mainnet` would read as an address.
        const named = block.attribution !== 'preimage'
        return (
          <div
            key={block.height}
            className={['ticker-row', mine ? 'mine' : '', fresh.has(block.height) ? 'fresh' : '']
              .filter(Boolean)
              .join(' ')}
          >
            {chain ? (
              <Link className="ticker-height" to={href({ chain, block: String(block.height) })}>
                #{height(block.height)}
              </Link>
            ) : (
              <span className="ticker-height">#{height(block.height)}</span>
            )}
            <Link
              to={href({ chain, miner: block.miner })}
              title={block.miner}
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontFamily: named ? 'inherit' : 'var(--font-mono)',
                fontSize: named ? 'inherit' : '12px',
                color: named ? 'var(--text-primary)' : 'var(--text-secondary)',
              }}
            >
              {block.display}
            </Link>
            <span className="ticker-gap">
              {block.gap_seconds !== null
                ? `+${seconds(block.gap_seconds)}`
                : ago(block.observed_at)}
            </span>
          </div>
        )
      })}
    </div>
  )
}
